/**
 * #1326 — what the sign-up prompt is allowed to promise.
 *
 * The bug this replaces: the lead line was a hardcoded constant reading
 * "Sign up today and get booking fees waived for your first three events."
 * It was shown on every sign-up surface regardless of
 * `first_booking_fee_waiver_enabled`, and the code waives exactly ONE
 * booking (`isEligibleForFirstBookingWaiver` — no prior CONFIRMED booking,
 * no prior waiver). So it over-promised threefold while the waiver was on,
 * and promised a waiver that would not happen at all once it was switched
 * off — which the founders decided to do for soft launch (2026-10-05).
 *
 * Pure so vitest can pin it without jsdom. Two rules:
 *   1. No fee claim unless the waiver is on AND the flags have loaded.
 *   2. The wording comes from the admin-managed message, never from here —
 *      a second hardcoded string is how the two drifted apart in the first
 *      place.
 */

/** Shown on every sign-up surface, with or without a fee offer. */
export const SIGNUP_INCENTIVE_REST =
  "Earn Path Coins to unlock discounts, vote on the community impact projects we support, save your preferences, get matched with the right events and facilitators, track your progress, and much more."

/** Stands alone when there is no offer, so it opens rather than continues. */
export const SIGNUP_INCENTIVE_LEAD_NO_OFFER = "Create a free account."

export interface FeeWaiverOffer {
  /** Is the waiver actually on? */
  enabled: boolean
  /** The admin-managed wording, or null. */
  message: string | null
  /** Have the flags arrived? Before they have, promise nothing. */
  loaded: boolean
}

/**
 * The bold lead line, or null when there is no offer to make.
 *
 * Returns null — rather than a fallback claim — whenever the waiver is off,
 * the flags have not loaded, or the admin message is empty. Silence is the
 * only safe failure here: the alternative is a promise.
 */
export function feeWaiverLead(offer: FeeWaiverOffer): string | null {
  if (!offer.loaded) return null
  if (!offer.enabled) return null
  const message = offer.message?.trim()
  return message ? message : null
}

/** The full prompt text, for surfaces that want one string. */
export function signupIncentiveCopy(offer: FeeWaiverOffer): string {
  const lead = feeWaiverLead(offer)
  return lead
    ? `${lead} ${SIGNUP_INCENTIVE_REST}`
    : `${SIGNUP_INCENTIVE_LEAD_NO_OFFER} ${SIGNUP_INCENTIVE_REST}`
}
